import React, { useEffect, useState, useRef } from "react";
import { createFileRoute } from "../lib/router-compat";
import { Info, X } from "lucide-react";
import { ClientOnly } from "../components/visualizer/ClientOnly";

declare global {
  interface Window {
    __vizDebug?: string[];
  }
}

export const Route = createFileRoute("/visualizer/")({
  head: () => ({
    meta: [
      { title: "Steelpan Visualizer — The Pan Institute" },
      {
        name: "description",
        content:
          "Interactive 3D visualizer of bass, tenor, and cello steelpans with custom finishes and lighting. Instruments by Nigel Chase at The Pan Institute.",
      },
      { property: "og:title", content: "Steelpan Visualizer — The Pan Institute" },
      {
        property: "og:description",
        content:
          "Explore bass, tenor, and cello steelpans in 3D — custom finishes and lighting. Instruments by Nigel Chase, The Pan Institute.",
      },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: VisualizerIndex,
});

function VisualizerIndex() {
  const [debugLines, setDebugLines] = useState<string[]>([]);
  const debugRef = useRef<(line: string) => void>(() => {});

  debugRef.current = (line: string) => {
    console.log("[visualizer]", line);
    window.__vizDebug = [...(window.__vizDebug || []), line].slice(-20);
    setDebugLines((prev) => [...prev.slice(-11), line]);
  };

  // Log mount once — use ref to avoid feedback loops
  useEffect(() => {
    debugRef.current?.("Visualizer route mounted");
    document.body.setAttribute("data-visualizer-route", "mounted");
    return () => {
      document.body.removeAttribute("data-visualizer-route");
    };
  }, []);

  return (
    <>
      <VisualizerBoundary onError={(msg) => debugRef.current?.(msg)}>
        <ClientOnly
          fallback={
            <div className="flex min-h-screen items-center justify-center bg-black">
              <p className="text-white/40 text-sm tracking-[0.3em] uppercase animate-pulse">
                Loading models…
              </p>
            </div>
          }
        />
      </VisualizerBoundary>
      <DebugPanel lines={debugLines} />
      <DevNote />
    </>
  );
}

class VisualizerBoundary extends React.Component<
  { children: React.ReactNode; onError: (message: string) => void },
  { error: Error | null }
> {
  state = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error) {
    this.props.onError(`Error boundary caught: ${error.message}`);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-black px-6">
          <div className="max-w-xl rounded-xl border border-red-500/30 bg-red-950/40 p-4 text-sm text-red-100">
            {this.state.error.message}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function DebugPanel({ lines }: { lines: string[] }) {
  return (
    <div
      id="visualizer-debug-panel"
      style={{
        position: "fixed",
        top: "16px",
        left: "16px",
        zIndex: 2147483647,
        width: "min(560px, calc(100vw - 32px))",
        background: "rgba(0,0,0,0.88)",
        color: "#b7ff9a",
        border: "2px solid rgba(128,255,128,0.45)",
        borderRadius: "12px",
        padding: "12px",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: "11px",
        lineHeight: 1.4,
        boxShadow: "0 12px 30px rgba(0,0,0,0.45)",
        pointerEvents: "none",
      }}
    >
      <div style={{ marginBottom: "8px", color: "#7dff7d", fontSize: "10px", letterSpacing: "0.2em", textTransform: "uppercase" }}>
        Visualizer Debug
      </div>
      <div>
        {lines.length ? lines.map((line, i) => <div key={i}>{line}</div>) : <div>Waiting for runtime events…</div>}
      </div>
    </div>
  );
}

function DevNote() {
  const [open, setOpen] = useState(false);
  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm">
      {open ? (
        <div className="rounded-xl border border-white/15 bg-black/70 backdrop-blur-md p-4 text-white/80 text-[12px] leading-relaxed shadow-xl">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/50">
              Dev note
            </div>
            <button
              type="button"
              aria-label="Close note"
              onClick={() => setOpen(false)}
              className="text-white/50 hover:text-white"
            >
              <X size={14} />
            </button>
          </div>
          <p>
            Adding this in, Vite was re-optimizing dependencies (since I'd just
            installed <code className="text-white">three</code>,{" "}
            <code className="text-white">@react-three/fiber</code>,{" "}
            <code className="text-white">@react-three/drei</code>), so the
            dynamic import of <code className="text-white">SteelpanScene.tsx</code>{" "}
            failed mid-restart. Once Vite finished, the module served fine — no
            code bug, just a transient state from the fresh install.
          </p>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-black/60 backdrop-blur-md px-3 py-2 text-[10px] uppercase tracking-[0.25em] text-white/70 hover:text-white hover:border-white/30 transition"
        >
          <Info size={12} /> Dev note
        </button>
      )}
    </div>
  );
}
